<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :refresh.sync="refreshTable"
            :actions="actions"
            ajax-path="postfix/domains"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit {{ editForm.domain }}</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.description"
                    placeholder="Description"
                    :error="editForm.errors.get('description')"
                    @updated="editForm.errors.clear('description')"
                    focus
                />
                <div class="flex items-center">
                    <app-input
                        v-model="editForm.aliases"
                        placeholder="Aliases"
                        :error="editForm.errors.get('aliases')"
                        @updated="editForm.errors.clear('aliases')"
                        required
                        class="flex-1"                   
                    />
                    <div class="ml-4 mt-3">
                        <radio 
                            v-model="editForm.aliases" 
                            :alt-value="-1"
                        >Disable</radio>
                        <radio 
                            v-model="editForm.aliases" 
                            :alt-value="0"
                            class="mt-3"
                        >Unlimited</radio>
                    </div>
                </div>
                <div class="flex items-center">
                    <app-input
                        v-model="editForm.mailboxes"
                        placeholder="Mailboxes"
                        :error="editForm.errors.get('mailboxes')"
                        @updated="editForm.errors.clear('mailboxes')"
                        required
                        v-integer
                        class="flex-1"                   
                    />
                    <div class="ml-4 mt-3">
                        <radio 
                            v-model="editForm.mailboxes" 
                            :alt-value="-1"
                        >Disable</radio>
                        <radio 
                            v-model="editForm.mailboxes" 
                            :alt-value="0"
                            class="mt-3"
                        >Unlimited</radio>
                    </div>
                </div>
                <checkbox 
                    v-model="editForm.backupmx"
                    class="mt-5"
                >Mail server is backup MX</checkbox>               
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Domain</template>
            <template slot="body">                
                <app-input
                    v-model="addForm.domain"
                    placeholder="Domain"
                    :error="addForm.errors.get('domain')"
                    @updated="addForm.errors.clear('domain')"
                    required
                    focus                   
                />
                <app-input
                    v-model="addForm.description"
                    placeholder="Description"
                    :error="addForm.errors.get('description')"
                    @updated="addForm.errors.clear('description')"
                />
                <div class="flex items-center">
                    <app-input
                        v-model="addForm.aliases"
                        placeholder="Aliases"
                        :error="addForm.errors.get('aliases')"
                        @updated="addForm.errors.clear('aliases')"
                        required
                        class="flex-1"                   
                    />
                    <div class="ml-4 mt-3">
                        <radio 
                            v-model="addForm.aliases" 
                            :alt-value="-1"
                        >Disable</radio>
                        <radio 
                            v-model="addForm.aliases" 
                            :alt-value="0"
                            class="mt-3"
                        >Unlimited</radio>
                    </div>
                </div>
                <div class="flex items-center">
                    <app-input
                        v-model="addForm.mailboxes"
                        placeholder="Mailboxes"
                        :error="addForm.errors.get('mailboxes')"
                        @updated="addForm.errors.clear('mailboxes')"
                        required
                        v-integer
                        class="flex-1"                   
                    />
                    <div class="ml-4 mt-3">
                        <radio 
                            v-model="addForm.mailboxes" 
                            :alt-value="-1"
                        >Disable</radio>
                        <radio 
                            v-model="addForm.mailboxes" 
                            :alt-value="0"
                            class="mt-3"
                        >Unlimited</radio>
                    </div>
                </div>
                <checkbox 
                    v-model="addForm.backupmx"
                    class="mt-5"
                >Mail server is backup MX</checkbox>                 
                <!--<checkbox 
                    v-model="addForm.default_aliases"
                    class="mt-5"
                >Add default mail aliases</checkbox>-->               
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAccountsModal"
            width="lg:w-5/6"
            custom-class="flex flex-col h-full"
        >
            <template slot="header">{{ selectedDomain.domain }} Accounts</template>
            <template slot="body">
                <mailboxes :domain="selectedDomain.domain"/>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAliasesModal"
            width="lg:w-5/6"
            custom-class="flex flex-col h-full"
        >
            <template slot="header">{{ selectedDomain.domain }} Aliases</template>
            <template slot="body">
                <aliases :domain="selectedDomain.domain"/>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import debounce from 'lodash/debounce';
    import StatusComponent from '@/components/Status.vue';
    import Mailboxes from '@/views/postfix/mailboxes/Index.vue';
    import Aliases from '@/views/postfix/aliases/Index.vue';

    export default {
        components: {
            StatusComponent,
            Mailboxes,
            Aliases,
        },
        data() {
            return {
                selectedDomain: {},
                addForm: new Form({
                    domain: null,
                    description: null,
                    aliases: 10,
                    mailboxes: 10,
                    backupmx: false,
                    active: true,
                    //default_aliases: false,
                }),
                editForm: new Form({
                    domain: null,
                    description: null,
                    aliases: 10,
                    mailboxes: 10,
                    backupmx: false,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                showAccountsModal: false,
                showAliasesModal: false,
                refreshTable: false,
                columns: [
                    {
                        title: 'Domain',
                        field: 'domain',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Description', 
                        field: 'description', 
                        sorter: 'string',
                        minWidth: 100,
                    },
                    {
                        title: 'Aliases',
                        field: 'aliases',
                        sorter: 'string',
                        minWidth: 100,
                        formatter({_cell}) {
                            return _cell.row.data.aliases_for_human;
                        }
                    },
                    {
                        title: 'Mailboxes',
                        field: 'mailboxes',
                        sorter: 'string',
                        minWidth: 100,
                        formatter({_cell}) {
                            return _cell.row.data.mailboxes_for_human;
                        }
                    },
                    {
                        title: 'Backup MX',
                        field: 'backupmx',
                        formatter({_cell}) {
                            if (_cell.value === 1) {
                                return 'Yes';
                            }

                            return 'No';
                        },
                        minWidth: 100,
                    },
                    {
                        title: 'Last Modified',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Status', 
                        field: 'active', 
                        formatter({_cell}) {
                            if (_cell.value) {
                                return '<span class="px-3 py-1 rounded-full shadow bg-green-200 text-green-700">Active</span>';
                            }

                            return '<span class="px-3 py-1 rounded-full shadow bg-red-200 text-red-700">Inactive</span>';
                        },
                        minWidth: 130,
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: domains => {
                            this.showEditModal = true;
                            this.editForm.set(domains[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Accounts',
                        icon: () => import('@/icons/AccountCircle.vue'),
                        handler: domains => {
                            this.showAccountsModal = true;
                            this.selectedDomain = domains[0];
                        }
                    },
                    {
                        label: 'Aliases',
                        icon: () => import('@/icons/TextFormat.vue'),
                        handler: domains => {
                            this.showAliasesModal = true;
                            this.selectedDomain = domains[0];
                        }
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: domains => {
                            this.activate(domains[0]);
                        },
                        isEligible(domains) {
                            return !domains[0].active;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: domains => {
                            this.deactivate(domains[0]);
                        },
                        isEligible(domains) {
                            return domains[0].active;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: domains => {
                            this.delete(domains[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],                
            }
        },
        methods: {            
            edit(domain) {
                this.showEditModal = true;
                this.editForm.set(domain);
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('postfix/domains/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.addForm.errors.clear();
                this.$store.dispatch('postfix/domains/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(domain) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${domain.domain}</strong>" domain.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/domains/deactivate', domain.domain);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(domain) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${domain.domain}</strong>" domain.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/domains/activate', domain.domain);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully activated.'
                        })
                    }
                })                
            },
            delete(domain) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${domain.domain}</strong>" domain permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/domains/delete', domain.domain);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully deleted.'
                        })
                    }
                })                
            },            
        }
    }
</script>